// Where the hoops pages live, and how a link to one is written. P6-329c (GN-2c).
//
// ═══════════════════════════════════════════════════════════════════════════
// THE ROUTES, AND WHY THEY ARE REAL PATHS
// ═══════════════════════════════════════════════════════════════════════════
//
//   /hoops                          redirects to /hoops/tonight (App.jsx: a section's own path is a real route)
//   /hoops/tonight                  the board, exactly as the portal's Hoops pill draws it
//   /hoops/players                  the index, with search
//   /hoops/players/:personId        one player's page
//   /hoops/leaders                  the top ten of each category
//   /hoops/standings                both conferences
//   /hoops/teams                    the thirty
//   /hoops/teams/:teamId            one team's page
//
// each of the season views taking `?season=2025-26` (the newest published season when it is absent or is not one),
// the leaders taking `?cat=blk`, and the players list taking `?q=doncic` (what was typed in its search box, so that Back
// returns to the list the member was looking at). They are paths and not component state because the point of these pages is to
// be SENT: a link to last season's blocks leaders, or to one player's two-season page, is something one friend
// pastes to another in Discord, and a phone's Back gesture has to land on the page before. (Tonight's GAME is
// still a history entry carrying state, as GN-1b built it: it is a screen change with one entrance, a card.)
//
// `/hoops` IS THE SECTION'S OWN PATH (`content-src/sections.json`, tier 3, a child of the portal so it is off the
// nav), which is what makes every path beneath it guarded the way `/watch/...` is: `functions/_lib/guard.js` matches
// "exact match, or anything beneath it". The router and the guard are asserted against EACH OTHER at every spelling
// react-router accepts, in `src/test/portal/hoops-routes.test.jsx` (CLAUDE.md gotcha 21).

import { validPersonId, validSeason, validTeamId } from './history.js'

export const HOOPS_BASE = '/hoops'

/** The tabs, in order: what each is called, the key it answers to, and where it goes. */
export const TABS = Object.freeze([
  Object.freeze({ key: 'tonight', label: 'Tonight', path: `${HOOPS_BASE}/tonight` }),
  Object.freeze({ key: 'players', label: 'Players', path: `${HOOPS_BASE}/players` }),
  Object.freeze({ key: 'leaders', label: 'Leaders', path: `${HOOPS_BASE}/leaders` }),
  Object.freeze({ key: 'standings', label: 'Standings', path: `${HOOPS_BASE}/standings` }),
  Object.freeze({ key: 'teams', label: 'Teams', path: `${HOOPS_BASE}/teams` }),
])

/** `?season=2025-26`, or nothing for a season that is not one (a link never carries a value the page would refuse). */
function seasonQuery(season, extra = {}) {
  const params = new URLSearchParams()
  if (validSeason(season) !== null) params.set('season', season)
  for (const [key, value] of Object.entries(extra)) if (value !== null && value !== undefined) params.set(key, value)
  const text = params.toString()
  return text === '' ? '' : `?${text}`
}

/** A list view: `listPath('leaders', '2024-25', { cat: 'blk' })` is `/hoops/leaders?season=2024-25&cat=blk`. */
export function listPath(tab, season, extra) {
  const found = TABS.find((entry) => entry.key === tab)
  return found === undefined ? null : `${found.path}${seasonQuery(season, extra)}`
}

/** One player's page, or `null` for an id that is not one. */
export function playerPath(personId, season) {
  const id = validPersonId(typeof personId === 'number' ? String(personId) : personId)
  return id === null ? null : `${HOOPS_BASE}/players/${id}${seasonQuery(season)}`
}

/** One team's page, or `null` for an id that is not one of the thirty. */
export function teamPath(teamId, season) {
  const id = validTeamId(typeof teamId === 'number' ? String(teamId) : teamId)
  return id === null ? null : `${HOOPS_BASE}/teams/${id}${seasonQuery(season)}`
}
